import { useEffect, useMemo, useRef, useState } from 'react';
import { mondayOf } from '../utils';

export default function InsightsTab({ insights, generateInsights }) {
  const triedAuto = useRef(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);

  const currentWeekStart = useMemo(() => mondayOf(new Date()).toISOString().slice(0, 10), []);
  const latest = useMemo(() => {
    return insights
      .slice()
      .sort((a, b) => (b.weekStartDate || '').localeCompare(a.weekStartDate || ''))[0];
  }, [insights]);

  async function generate() {
    setGenerating(true);
    setError(null);
    try {
      await generateInsights();
    } catch (err) {
      setError(err.message || 'Could not generate digest');
    } finally {
      setGenerating(false);
    }
  }

  useEffect(() => {
    if (triedAuto.current) return;
    if (generating) return;
    if (!latest || latest.weekStartDate !== currentWeekStart) {
      triedAuto.current = true;
      generate();
    } else {
      triedAuto.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latest, currentWeekStart]);

  const isCurrent = latest?.weekStartDate === currentWeekStart;

  return (
    <div className="insights-tab">
      <div className="tab-head">
        <h3>Weekly digest</h3>
        <button className="link-btn" onClick={generate} disabled={generating}>
          {generating ? 'Thinking…' : isCurrent ? 'Regenerate' : 'Generate'}
        </button>
      </div>
      {error && (
        <p className="error-text" role="alert">
          {error}
        </p>
      )}
      {!latest && !generating && <p className="muted">No digest yet.</p>}
      {latest && (
        <div className="insight-cards">
          {!isCurrent && (
            <p className="muted">
              Showing the digest from week of {latest.weekStartDate}. Click Generate for this week.
            </p>
          )}
          <InsightCard title="Mood" body={latest.moodSummary} />
          <InsightCard title="Habits" body={latest.habitSummary} />
          <InsightCard title="Goals" body={latest.goalSummary} />
          <InsightCard title="Try this" body={latest.suggestion} accent />
          <InsightCard title="Worth saying" body={latest.encouragement} accent />
        </div>
      )}
    </div>
  );
}

function InsightCard({ title, body, accent }) {
  if (!body) return null;
  return (
    <article className={`insight-card${accent ? ' insight-card--accent' : ''}`}>
      <h4>{title}</h4>
      <p>{body}</p>
    </article>
  );
}
